import React, { useState, useRef, useEffect, useCallback } from "react";

const FlagSelectTailwind = ({
  value,
  onChange,
  options = [],
  placeholder = "Nation auswählen...",
  disabled = false,
  allOptionLabel = null  // z.B. "Alle" – macht "Alle" zur echten auswählbaren Option
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionRefs = useRef([]);

  // Virtuelle "Alle"-Option am Anfang, wenn allOptionLabel gesetzt ist
  const allOption = allOptionLabel ? { code: "", name: allOptionLabel, isAll: true } : null;
  const displayOptions = allOption ? [allOption, ...options] : options;
  const selectedOption = displayOptions.find(option => option.code === value);

  // "Alle"-Option immer zeigen (kein Filter), echte Nationen filtern
  const filteredNations = options.filter(option =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.code.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredOptions = allOption
    ? (searchTerm ? filteredNations : [allOption, ...filteredNations])
    : filteredNations;

  // Schließe Dropdown beim Klicken außerhalb
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
        setFocusedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fokussierte Option ins Sichtfeld scrollen
  useEffect(() => {
    if (focusedIndex >= 0 && optionRefs.current[focusedIndex]) {
      optionRefs.current[focusedIndex].scrollIntoView({ block: "nearest" });
    }
  }, [focusedIndex]);

  // focusedIndex beim Filtern zurücksetzen
  useEffect(() => {
    if (isOpen) {
      const selIdx = filteredOptions.findIndex(o => o.code === value);
      setFocusedIndex(selIdx >= 0 ? selIdx : filteredOptions.length > 0 ? 0 : -1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const openDropdown = useCallback(() => {
    if (disabled) return;
    setSearchTerm("");
    const selIdx = displayOptions.findIndex(o => o.code === value);
    setFocusedIndex(selIdx >= 0 ? selIdx : 0);
    setIsOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 10);
  }, [disabled, options, value]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchTerm("");
    setFocusedIndex(-1);
  }, []);

  const handleOptionSelect = (option) => {
    onChange(option.code);
    closeDropdown();
  };

  const handleToggle = () => {
    if (isOpen) closeDropdown();
    else openDropdown();
  };

  // Tastatursteuerung am Trigger-Button
  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!isOpen) {
        openDropdown();
      } else if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
        handleOptionSelect(filteredOptions[focusedIndex]);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) openDropdown();
      else setFocusedIndex(i => Math.min(i + 1, filteredOptions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!isOpen) openDropdown();
      else setFocusedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeDropdown();
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      setSearchTerm(e.key);
      setIsOpen(true);
      setTimeout(() => searchInputRef.current?.focus(), 10);
    }
  };

  // Tastatursteuerung im Suchfeld
  const handleSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex(i => Math.min(i + 1, filteredOptions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
        handleOptionSelect(filteredOptions[focusedIndex]);
      } else if (filteredOptions.length === 1) {
        handleOptionSelect(filteredOptions[0]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeDropdown();
    } else if (e.key === "Tab") {
      closeDropdown();
    }
  };

  const FlagImage = ({ code, name, size = "w-6 h-6" }) => (
    <div className={`${size} relative flex-shrink-0`}>
      <img
        src={process.env.PUBLIC_URL + "/images/flags/" + code + ".png"}
        alt={name}
        className={`${size} object-cover rounded-full border border-slate-200 dark:border-slate-600`}
        onError={(e) => {
          e.target.style.display = "none";
          e.target.nextElementSibling.style.display = "flex";
        }}
      />
      <div
        className={`${size} rounded-full bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 items-center justify-center hidden`}
        style={{ position: "absolute", top: 0, left: 0 }}
      >
        <i className="fas fa-globe text-slate-400 dark:text-slate-500 text-[10px]" />
      </div>
    </div>
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger */}
      <button
        type="button"
        onClick={handleToggle}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
        className={`
          w-full h-10 px-3 text-left flex items-center gap-2
          border rounded-xl transition-colors
          focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20
          focus:border-primary dark:focus:border-primary-light
          ${disabled
            ? "bg-slate-50 dark:bg-slate-700/50 text-slate-400 dark:text-slate-500 cursor-not-allowed border-slate-200 dark:border-slate-600"
            : "bg-slate-50 dark:bg-slate-700/50 text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500"
          }
        `}
      >
        {selectedOption && !selectedOption.isAll ? (
          <FlagImage code={selectedOption.code} name={selectedOption.name} />
        ) : (
          <i className={`fas fa-globe text-sm flex-shrink-0 ${selectedOption ? "text-slate-500 dark:text-slate-400" : "text-slate-400 dark:text-slate-500"}`} />
        )}
        <span className={`flex-1 text-sm truncate ${selectedOption ? "font-medium" : "text-slate-400 dark:text-slate-500"}`}>
          {selectedOption ? selectedOption.name : placeholder}
        </span>
        {selectedOption && !selectedOption.isAll && (
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 flex-shrink-0">
            {selectedOption.code}
          </span>
        )}
        <i className={`fas fa-chevron-down flex-shrink-0 text-slate-400 dark:text-slate-500 text-[10px] transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown-Panel */}
      {isOpen && (
        <div className="absolute z-[99999] w-full min-w-[14rem] mt-1.5 top-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden">
          {/* Suchfeld */}
          <div className="p-2 border-b border-slate-100 dark:border-slate-700/60">
            <div className="relative">
              <i className="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-[10px] pointer-events-none" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Nation suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="w-full h-8 pl-7 pr-3 text-xs border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                autoFocus
              />
            </div>
          </div>

          {/* Optionsliste */}
          <div className="max-h-52 overflow-y-auto py-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, idx) => {
                const isSelected = value === option.code;
                const isFocused = focusedIndex === idx;
                return (
                  <button
                    key={option.id || option.code || "__all__"}
                    ref={el => { optionRefs.current[idx] = el; }}
                    type="button"
                    onClick={() => handleOptionSelect(option)}
                    onMouseEnter={() => setFocusedIndex(idx)}
                    className={`
                      w-full px-3 py-2 text-left flex items-center gap-2.5
                      focus:outline-none transition-colors
                      ${isSelected
                        ? "bg-primary/8 dark:bg-primary-light/10"
                        : isFocused
                          ? "bg-slate-100 dark:bg-slate-700"
                          : "hover:bg-slate-50 dark:hover:bg-slate-700/50"
                      }
                    `}
                  >
                    {option.isAll ? (
                      <span className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`fas fa-globe text-sm ${isSelected ? "text-primary dark:text-primary-light" : "text-slate-400 dark:text-slate-500"}`} />
                      </span>
                    ) : (
                      <FlagImage code={option.code} name={option.name} />
                    )}
                    <span className={`flex-1 text-sm truncate ${isSelected ? "font-semibold text-primary dark:text-primary-light" : "font-medium text-slate-700 dark:text-slate-300"}`}>
                      {option.name}
                    </span>
                    {!option.isAll && (
                      <span className={`text-[10px] font-semibold uppercase tracking-widest flex-shrink-0 ${isSelected ? "text-primary/60 dark:text-primary-light/60" : "text-slate-400 dark:text-slate-500"}`}>
                        {option.code}
                      </span>
                    )}
                    {isSelected && (
                      <i className="fas fa-check text-primary dark:text-primary-light text-[10px] flex-shrink-0" />
                    )}
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-3 text-xs text-slate-400 dark:text-slate-500 text-center">
                Keine Nationen gefunden
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlagSelectTailwind;
